import styled from 'styled-components/macro';
import {useApprove, useIgnoreReports, useLock, useModDistinguish, useRemove, useStickied} from "../api/moderation";
import {useFeedback} from "../store/feedback";
import {useUsername} from "../store/hooks";
import {useNSFW} from "../api/nsfw";
import {formatRelativeTime} from "../utils/calendar";
import {useActions} from "../store";
import Tooltip from "./tooltip";
import {TextButton} from "./button";
import ConfirmButton from "./confirm_button";
import {useRetryPreview} from "../api/retry_preview";
import {themecolors} from "../utils/themecolors";

const Action = styled.div`
  ${TextButton} {
    padding: 2px 12px;
    border-radius: 2px;
  }
  color: ${({theme}) => themecolors.light_high_impact_text};
;`
const Reported = styled(Action)`
  ${TextButton} {
    background-color: #ffebc2;
  }
;`

const Approve = styled(Action)`
  ${TextButton} {
    background-color: #d1eabf;
  }
;`
const IgnoreReports = styled(Action)`
  ${TextButton} {
    background-color: #ececec;
  }
;`
const Remove = styled(Action)`
  ${TextButton} {
    background-color: #edbdbe;
  }
;`
const Removed = styled(Action)`
  font-weight: 500;
  margin-right: 6px;
  color: ${({theme}) => themecolors.light_high_impact_text};
;`

const ModActions = ({post, comment, setReports, reports}) => {

    const actions = useActions();

    const username = useUsername();
    const author = post ? post.author : comment.author;

    const moderation = post ? post.moderation : comment.moderation;
    const removed = moderation.removed_at !== 0;
    const removedAt = moderation.removed_at;
    const approved = moderation.approved_at !== 0;
    const ignoredReports = moderation.is_ignored_reports;
    const isReported = moderation.is_reported;
    const isLocked = post && post.is_locked;
    const isStickied = post ? post.is_stickied : comment.is_stickied;
    const isModDistinguished = post ? post.is_moderator : comment.is_moderator;
    const removedBy = post ? moderation.removed_by : moderation.removed_by;
    const isNSFW = post && post.is_nsfw;
    const isRetryable = post && moderation.is_retryable_preview;

    const id = post ? post.id : comment.id;
    const type = post ? "post" : "comment";

    const {feedbackCh} = useFeedback();

    const ignore_reports = useIgnoreReports(feedbackCh);
    const handleIgnoreReports = async () => {
        const action = type === 'post' ? actions.postIgnoreReports(id, !ignoredReports) : actions.commentIgnoreReports(id, !ignoredReports);
        try {
            await ignore_reports(id, type, !ignoredReports);
        } catch {
            action.undo?.();
        }
    };

    const approve = useApprove(feedbackCh);
    const handleApprove = async () => {
        const action = type === 'post' ? actions.postApprove(id, username) : actions.commentApprove(id, username);
        try {
            await approve(id, type);
        } catch {
            action.undo?.();
        }
    };

    const remove = useRemove(feedbackCh);
    const handleRemove = async () => {
        const action = type === 'post' ? actions.postRemove(id, username) : actions.commentRemove(id, username);
        try {
            await remove(id, type);
        } catch {
            action.undo?.();
        }
    };

    const lock = useLock(feedbackCh);
    const handleLock = async () => {
        const action = actions.postLock(id, !isLocked);
        try {
            await lock(id, type, !isLocked);
        } catch {
            action.undo?.();
        }
    };

    const sticky = useStickied(feedbackCh);
    const handleSticky = async () => {
        const action = type === 'post' ? actions.postSticky(id, !isStickied) : actions.commentSticky(id, !isStickied);
        try {
            await sticky(id, type, !isStickied);
        } catch {
            action.undo?.();
        }
    };

    const modDistinguish = useModDistinguish(feedbackCh);
    const handleModDistinguish = async () => {
        const action = type === 'post' ? actions.postModDistinguished(id, !isModDistinguished) : actions.commentModDistinguished(id, !isModDistinguished);
        try {
            await modDistinguish(id, type, !isModDistinguished);
        } catch {
            action.undo?.();
        }
    };

    const nsfw = useNSFW(feedbackCh);
    const handleNSFW = async () => {
        const action = actions.postNsfw(id, !isNSFW);
        try {
            await nsfw(id, !isNSFW);
        } catch (ex) {
            action.undo?.();
        }
    };

    const handleReports = () => {
        setReports(!reports);
    };

    const retry = useRetryPreview(feedbackCh);
    const handleRetry = async () => {
        const action = actions.postRetryablePreview(id);
        try {
            await retry(id, type, !isLocked);
        } catch {
            action.undo?.();
        }
    };

    return (
        <>

            {author !== username && post && (
                <TextButton onClick={handleNSFW}>{isNSFW && 'un'}nsfw</TextButton>
            )}
            {((comment?.comment_parent_id == 0 && comment?.author === username) || post) && (
                <ConfirmButton onClick={handleSticky}>{isStickied && 'un'}sticky</ConfirmButton>
            )}
            {author === username && (
                <TextButton onClick={handleModDistinguish}>{isModDistinguished && 'un'}distinguish</TextButton>
            )}

            {post && (
                isLocked ? (
                    <TextButton onClick={handleLock}>unlock</TextButton>
                ) : (
                    <ConfirmButton onClick={handleLock}>lock</ConfirmButton>
                )
            )}

            {post && (
                isRetryable && (
                    <TextButton onClick={handleRetry}>retry preview</TextButton>
                )
            )}

            {isReported && (<>
                <Remove>
                    <TextButton onClick={handleRemove}>remove{removed && 'd'}</TextButton>
                </Remove>
                <Approve>
                    <TextButton onClick={handleApprove}>approve{approved && 'd'}</TextButton>
                </Approve>
                <IgnoreReports>
                    <TextButton onClick={handleIgnoreReports}>{ignoredReports && 'un'}ignore reports</TextButton>
                </IgnoreReports>
                <Reported>
                    <TextButton onClick={handleReports}>reported</TextButton>
                </Reported>
            </>)}

            {!isReported && (<>
                <ConfirmButton onClick={handleRemove}>remove{removed && 'd'}</ConfirmButton>
                {removed && (
                    <Approve>
                        <TextButton onClick={handleApprove}>approve{approved && 'd'}</TextButton>
                    </Approve>
                )}
                <TextButton onClick={handleIgnoreReports}>{ignoredReports && 'un'}ignore reports</TextButton>
                {removed && (
                    <Tooltip content={`Removed ${formatRelativeTime(removedAt)} ago by ${removedBy} `}>
                        <Removed>
                            (removed by {post ? post.moderation.removed_by : comment.moderation.removed_by})
                        </Removed>
                    </Tooltip>
                )}
            </>)}

        </>
    );
};

export default ModActions;
